import { blankPpf, ppfOutput, validatePpf, type PpfDraft } from "./ppf-model";
// The entries an owner SUPPLIES for kits whose collections are not stored records (directory listings, wiki references and related entries, area guides,
// authors, magazine formats), as a structured draft the Site screen edits with ordinary fields, plus the pure functions that turn it into the
// existing `supplied` request object, check it, and read an imported JSON object back into it. Nothing here invents a value: a blank field is
// omitted, a blank row is ignored, and the request object holds exactly what was typed.
//
// Paths are dotted ("directory.listings.0.category"); the server's own error paths ("supplied.directory.listings[0].category") are mapped onto them
// so the composer's refusal is shown beside the field that caused it.

export type SuppliedKey = "ppf" | "authors" | "directory" | "wiki" | "guide" | "magazine" | "cases" | "newsroom";
export const SUPPLIED_KEYS: readonly SuppliedKey[] = ["ppf","authors", "directory", "wiki", "guide", "magazine", "cases", "newsroom"];
export const isSuppliedKey = (k: string): k is SuppliedKey => (SUPPLIED_KEYS as readonly string[]).includes(k);

export interface Choices {
  /** Only approved image revisions pinned to this site. */
  media?: {id:string;label:string}[];
  /** Approved content of this client (id and title): what an author wrote, which entry is related, which item is a guide. */
  content: { id: string; title: string; topic?: string }[];
  /** Approved service names of this client. */
  services: string[];
}

export interface NameDesc { name: string; description: string }
export interface Listing { name: string; category: string; place: string; summary: string; description: string; offerings: string; phone: string; email: string; addressLine: string; websiteUrl: string; featured: boolean; confirmedOn: string }
export interface Author { name: string; bio: string; contentIds: string[] }
export interface TopicNote { topic: string; description: string }
export interface Reference { label: string; detail: string; url: string }
export interface WikiMeta { contentId: string; references: string[]; related: string[]; updatedOn: string }
export interface Area { name: string; summary: string; body: string; services: string[]; nearby: string }
export interface GuideLink { contentId: string; services: string[]; areas: string[] }
export interface ItemMeta { contentId: string; format: "" | "article" | "guide" | "resource"; featured: boolean }

export interface Project { title:string; summary:string; challenge:string; approach:string; outcome:string; proofUrl:string; completedOn:string }
export interface ReleaseDate { contentId:string; issuedOn:string }
export const blankProject=():Project=>({title:'',summary:'',challenge:'',approach:'',outcome:'',proofUrl:'',completedOn:''});
export const blankReleaseDate=():ReleaseDate=>({contentId:'',issuedOn:''});
export interface SuppliedDraft {
  ppf:PpfDraft;
  projects:Project[];
  releases:ReleaseDate[];
  authors: Author[];
  topics: TopicNote[];
  categories: NameDesc[];
  places: NameDesc[];
  listings: Listing[];
  references: Reference[];
  wikiEntries: WikiMeta[];
  areas: Area[];
  guideLinks: GuideLink[];
  items: ItemMeta[];
}
export const emptySupplied = (): SuppliedDraft => ({ ppf:blankPpf(), projects:[], releases:[], authors: [], topics: [], categories: [], places: [], listings: [], references: [], wikiEntries: [], areas: [], guideLinks: [], items: [] });

export const blankListing = (): Listing => ({ name: "", category: "", place: "", summary: "", description: "", offerings: "", phone: "", email: "", addressLine: "", websiteUrl: "", featured: false, confirmedOn: "" });
export const blankNameDesc = (): NameDesc => ({ name: "", description: "" });
export const blankAuthor = (): Author => ({ name: "", bio: "", contentIds: [] });
export const blankTopic = (): TopicNote => ({ topic: "", description: "" });
export const blankReference = (): Reference => ({ label: "", detail: "", url: "" });
export const blankWikiMeta = (): WikiMeta => ({ contentId: "", references: [], related: [], updatedOn: "" });
export const blankArea = (): Area => ({ name: "", summary: "", body: "", services: [], nearby: "" });
export const blankGuideLink = (): GuideLink => ({ contentId: "", services: [], areas: [] });
export const blankItem = (): ItemMeta => ({ contentId: "", format: "", featured: false });

export const MIN_AREA_WORDS = 80;
export const wordCount = (t: string): number => (t.trim() === "" ? 0 : t.trim().split(/\s+/).length);

const t = (s: string): string => s.trim();
const lines = (s: string): string[] => s.split(/\r?\n/).map(t).filter(Boolean);
const isEmpty = (o: object): boolean => Object.values(o).every((v) => (Array.isArray(v) ? v.length === 0 : typeof v === "boolean" ? v === false : t(String(v)) === ""));
/** Rows with at least one thing entered. A fully blank row is dropped silently: adding a row and not filling it is not an error. */
const used = <T extends object>(rows: T[]): { row: T; index: number }[] => rows.map((row, index) => ({ row, index })).filter((x) => !isEmpty(x.row));

const put = <T extends Record<string, unknown>>(o: T, k: string, v: unknown): void => { if (v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)) (o as Record<string, unknown>)[k] = v; };

const authorsOut = (rows: Author[]) => used(rows).map(({ row }) => { const o: Record<string, unknown> = { name: t(row.name) }; put(o, "bio", t(row.bio)); put(o, "masterIds", row.contentIds); return o; });
const topicsOut = (rows: TopicNote[]): Record<string, string> => Object.fromEntries(used(rows).filter(({ row }) => t(row.topic) && t(row.description)).map(({ row }) => [t(row.topic), t(row.description)]));

/** The `supplied` request object for one kit, or null when nothing was entered. Only what was typed is included. */
export function toSupplied(key: SuppliedKey, d: SuppliedDraft): Record<string, unknown> | null {
  if (key === "ppf") return ppfOutput(d.ppf);
  let out: Record<string, unknown> | null = null;
  if (key === "authors") {
    const o: Record<string, unknown> = {};
    put(o, "authors", authorsOut(d.authors));
    put(o, "topicDescriptions", Object.keys(topicsOut(d.topics)).length ? topicsOut(d.topics) : undefined);
    out = o;
  } else if (key === "directory") {
    const dir: Record<string, unknown> = {};
    put(dir, "categories", used(d.categories).map(({ row }) => ({ name: t(row.name), description: t(row.description) })));
    put(dir, "places", used(d.places).map(({ row }) => ({ name: t(row.name), description: t(row.description) })));
    put(dir, "listings", used(d.listings).map(({ row: l }) => {
      const o: Record<string, unknown> = { name: t(l.name), category: t(l.category) };
      put(o, "place", t(l.place)); put(o, "summary", t(l.summary));
      const paras = t(l.description).split(/\n{2,}/).map((p) => p.replace(/\s+/g, " ").trim()).filter(Boolean);
      put(o, "description", paras); put(o, "offerings", lines(l.offerings)); put(o, "phone", t(l.phone)); put(o, "email", t(l.email)); put(o, "addressLine", t(l.addressLine));
      put(o, "websiteUrl", t(l.websiteUrl)); if (l.featured) o.featured = true; put(o, "confirmedOn", t(l.confirmedOn));
      return o;
    }));
    out = Object.keys(dir).length ? { directory: dir } : {};
  } else if (key === "wiki") {
    const w: Record<string, unknown> = {};
    put(w, "references", used(d.references).map(({ row }) => { const o: Record<string, unknown> = { label: t(row.label) }; put(o, "detail", t(row.detail)); put(o, "url", t(row.url)); return o; }));
    const entries: Record<string, unknown> = {};
    for (const { row } of used(d.wikiEntries)) {
      if (!t(row.contentId)) continue;
      const o: Record<string, unknown> = {};
      put(o, "references", row.references); put(o, "related", row.related); put(o, "updatedOn", t(row.updatedOn));
      if (Object.keys(o).length) entries[row.contentId] = o;
    }
    put(w, "entries", Object.keys(entries).length ? entries : undefined);
    put(w, "topicDescriptions", Object.keys(topicsOut(d.topics)).length ? topicsOut(d.topics) : undefined);
    out = Object.keys(w).length ? { wiki: w } : {};
  } else if (key === "guide") {
    const g: Record<string, unknown> = {};
    put(g, "areas", used(d.areas).map(({ row: a }) => {
      const o: Record<string, unknown> = { name: t(a.name), summary: t(a.summary) };
      const paras = t(a.body).split(/\n{2,}/).map((p) => p.replace(/\s+/g, " ").trim()).filter(Boolean);
      put(o, "body", paras); put(o, "services", a.services); put(o, "nearby", lines(a.nearby));
      return o;
    }));
    const res: Record<string, unknown> = {};
    for (const { row } of used(d.guideLinks)) {
      if (!t(row.contentId)) continue;
      const o: Record<string, unknown> = {};
      put(o, "services", row.services); put(o, "areas", row.areas);
      if (Object.keys(o).length) res[row.contentId] = o;
    }
    put(g, "resources", Object.keys(res).length ? res : undefined);
    out = Object.keys(g).length ? { guide: g } : {};
  } else if (key === "cases") {
    const projects=used(d.projects).map(({row})=>{
      const p:Record<string,unknown>={title:t(row.title),summary:t(row.summary)};
      for(const k of ['challenge','approach','outcome'] as const)p[k]=t(row[k]).split(/\n{2,}/).map(p=>p.replace(/\s+/g,' ').trim()).filter(Boolean);
      put(p,'proofUrl',t(row.proofUrl));put(p,'completedOn',t(row.completedOn));return p;
    });
    out=projects.length?{cases:{projects}}:{};
  } else if (key === "newsroom") {
    const releases=Object.fromEntries(used(d.releases).filter(({row})=>t(row.contentId)).map(({row})=>[row.contentId,{issuedOn:t(row.issuedOn)}]));
    out=Object.keys(releases).length?{newsroom:{releases}}:{};
  } else {
    const m: Record<string, unknown> = {};
    const items: Record<string, unknown> = {};
    for (const { row } of used(d.items)) {
      if (!t(row.contentId)) continue;
      const o: Record<string, unknown> = {};
      put(o, "format", row.format); if (row.featured) o.featured = true;
      if (Object.keys(o).length) items[row.contentId] = o;
    }
    put(m, "items", Object.keys(items).length ? items : undefined);
    put(m, "authors", authorsOut(d.authors));
    put(m, "topicDescriptions", Object.keys(topicsOut(d.topics)).length ? topicsOut(d.topics) : undefined);
    out = Object.keys(m).length ? { magazine: m } : {};
  }
  return out && Object.keys(out).length ? out : null;
}

export type FieldErrors = Record<string, string>;

const URL_OK = (v: string): boolean => { try { const u = new URL(v); return u.protocol === "https:" || u.protocol === "http:"; } catch { return false; } };
const REAL_DATE=(v:string):boolean=>/^\d{4}-\d{2}-\d{2}$/.test(v)&&Number.isFinite(Date.parse(v))&&new Date(v).toISOString().slice(0,10)===v;
const DATE_OK = (v: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`));
const EMAIL_OK = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

/** Problems the page can see before anything is sent, keyed by field path. `required` says whether the kit needs this input at all. */
export function validateSupplied(key: SuppliedKey, d: SuppliedDraft, required: boolean): FieldErrors {
  if(key === "ppf") return validatePpf(d.ppf);
  const e: FieldErrors = {};
  const need = (path: string, v: string, what: string) => { if (t(v) === "") e[path] = `${what} is required.`; };
  const dup = (rows: { name: string }[], path: string, label: string) => {
    const seen = new Map<string, number>();
    rows.forEach((r, i) => { const k = t(r.name).toLowerCase(); if (!k) return; if (seen.has(k)) e[`${path}.${i}.name`] = `Another ${label} already has this name. Names must differ because listings refer to them by name.`; else seen.set(k, i); });
  };
  const authors = () => used(d.authors).forEach(({ row, index }) => need(`authors.${index}.name`, row.name, "The author's name"));
  const topics = () => used(d.topics).forEach(({ row, index }) => { if (!t(row.topic)) e[`topics.${index}.topic`] = "Say which topic this describes."; if (!t(row.description)) e[`topics.${index}.description`] = "Write the description, or remove this row."; });
  if (key === "authors" || key === "magazine") authors();
  if (key === "authors" || key === "wiki" || key === "magazine") topics();
  if (key === "directory") {
    const cats = used(d.categories), places = used(d.places), listings = used(d.listings);
    if (required && cats.length === 0) e.categories = "Add at least one category. Every listing belongs to one.";
    if (required && listings.length === 0) e.listings = "Add at least one listing. A directory contains only the entries you supply.";
    cats.forEach(({ row, index }) => { need(`categories.${index}.name`, row.name, "The category name"); need(`categories.${index}.description`, row.description, "A description"); });
    places.forEach(({ row, index }) => { need(`places.${index}.name`, row.name, "The area name"); need(`places.${index}.description`, row.description, "A description"); });
    dup(cats.map((x) => x.row).map((r) => ({ name: r.name })), "categories", "category");
    const catNames = new Set(cats.map((x) => t(x.row.name)));
    const placeNames = new Set(places.map((x) => t(x.row.name)));
    listings.forEach(({ row: l, index: i }) => {
      need(`listings.${i}.name`, l.name, "The business name");
      need(`listings.${i}.summary`, l.summary, "A one-line summary");
      need(`listings.${i}.description`, l.description, "A description");
      if (t(l.category) === "") e[`listings.${i}.category`] = catNames.size ? "Choose the category." : "Add a category above first, then choose it here.";
      else if (!catNames.has(t(l.category))) e[`listings.${i}.category`] = "This category is no longer in the list above. Choose another.";
      if (t(l.place) && !placeNames.has(t(l.place))) e[`listings.${i}.place`] = "This area is no longer in the list above. Choose another or leave it empty.";
      if (t(l.websiteUrl) && !URL_OK(t(l.websiteUrl))) e[`listings.${i}.websiteUrl`] = "Use a full web address starting with https://.";
      if (t(l.email) && !EMAIL_OK(t(l.email))) e[`listings.${i}.email`] = "Use an address like name@business.example.";
      if (t(l.confirmedOn) && !DATE_OK(t(l.confirmedOn))) e[`listings.${i}.confirmedOn`] = "Use a date like 2026-09-30.";
    });
    dup(listings.map((x) => x.row), "listings", "listing");
  }
  if (key === "wiki") {
    const refs = used(d.references);
    refs.forEach(({ row, index }) => { need(`references.${index}.label`, row.label, "The reference label"); if (t(row.url) && !URL_OK(t(row.url))) e[`references.${index}.url`] = "Use a full web address starting with https://."; });
    const seen = new Map<string, number>();
    refs.forEach(({ row, index }) => { const k = t(row.label).toLowerCase(); if (!k) return; if (seen.has(k)) e[`references.${index}.label`] = "Another reference has this label. Entries pick references by label, so labels must differ."; else seen.set(k, index); });
    const labels = new Set(refs.map((x) => t(x.row.label)));
    const ids = new Set<string>();
    used(d.wikiEntries).forEach(({ row, index }) => {
      if (t(row.contentId) === "") e[`wikiEntries.${index}.contentId`] = "Choose which entry these details are for.";
      else if (ids.has(row.contentId)) e[`wikiEntries.${index}.contentId`] = "This entry already has a row. Edit that one instead.";
      else ids.add(row.contentId);
      if (row.references.some((l) => !labels.has(l))) e[`wikiEntries.${index}.references`] = "A chosen reference is no longer in the list above. Untick it or add it back.";
      if (row.related.includes(row.contentId)) e[`wikiEntries.${index}.related`] = "An entry cannot be related to itself.";
      if (t(row.updatedOn) && !DATE_OK(t(row.updatedOn))) e[`wikiEntries.${index}.updatedOn`] = "Use a date like 2026-09-30.";
    });
  }
  if (key === "guide") {
    const areas = used(d.areas);
    if (required && areas.length === 0) e.areas = "Add at least one service area. Each gets its own page, with text written for that place.";
    areas.forEach(({ row, index }) => {
      need(`areas.${index}.name`, row.name, "The area name"); need(`areas.${index}.summary`, row.summary, "A short summary");
      const w = wordCount(row.body);
      if (w === 0) e[`areas.${index}.body`] = "The area guide text is required.";
      else if (w < MIN_AREA_WORDS) e[`areas.${index}.body`] = `${w} of ${MIN_AREA_WORDS} words. An area page needs at least ${MIN_AREA_WORDS} words that say something specific to this place.`;
    });
    dup(areas.map((x) => x.row), "areas", "area");
    const names = new Set(areas.map((x) => t(x.row.name)));
    const ids = new Set<string>();
    used(d.guideLinks).forEach(({ row, index }) => {
      if (t(row.contentId) === "") e[`guideLinks.${index}.contentId`] = "Choose which resource this is for.";
      else if (ids.has(row.contentId)) e[`guideLinks.${index}.contentId`] = "This resource already has a row. Edit that one instead.";
      else ids.add(row.contentId);
      if (row.areas.some((a) => !names.has(a))) e[`guideLinks.${index}.areas`] = "A chosen area is no longer in the list above. Untick it or add it back.";
    });
  }
  if (key === "magazine") {
    const ids = new Set<string>();
    used(d.items).forEach(({ row, index }) => {
      if (t(row.contentId) === "") e[`items.${index}.contentId`] = "Choose which item this is for.";
      else if (ids.has(row.contentId)) e[`items.${index}.contentId`] = "This item already has a row. Edit that one instead.";
      else ids.add(row.contentId);
    });
  }
  if(key==='cases') {
    const projects=used(d.projects);
    if(required&&!projects.length)e.projects='Add at least one approved project.';
    projects.forEach(({row,index})=>{
      for(const field of ['title','summary','challenge','approach','outcome'] as const)need(`projects.${index}.${field}`,row[field],field==='outcome'?'The reported outcome':field);
      if(t(row.proofUrl)&&!URL_OK(t(row.proofUrl)))e[`projects.${index}.proofUrl`]='Use an approved absolute HTTP(S) evidence URL.';
      if(t(row.completedOn)&&!REAL_DATE(t(row.completedOn)))e[`projects.${index}.completedOn`]='Use a real YYYY-MM-DD completion date.';
    });
  }
  if(key==='newsroom') {
    const ids=new Set<string>(),releases=used(d.releases);
    if(required&&!releases.length)e.releases='Add the actual issue date for each approved release.';
    releases.forEach(({row,index})=>{
      need(`releases.${index}.contentId`,row.contentId,'The approved release');
      if(ids.has(row.contentId))e[`releases.${index}.contentId`]='This release already has a row.';ids.add(row.contentId);
      if(!REAL_DATE(t(row.issuedOn)))e[`releases.${index}.issuedOn`]='Supply the actual issue date as YYYY-MM-DD.';
    });
  }
  return e;
}

/** Everything under a section path, for "this section has a problem" summaries. */
export const errorsUnder = (e: FieldErrors, prefix: string): string[] => Object.entries(e).filter(([k]) => k === prefix || k.startsWith(`${prefix}.`)).map(([, v]) => v);

/**
 * The server's error path onto the page's: "supplied.directory.listings[0].category" -> "listings.0.category". Blank rows are never sent, so the
 * server's row number counts only the rows that were sent; it is mapped back to the row on screen. Sections keyed by content id map to the row
 * that has that id. A path the page has no field for maps to the section (or "" for the whole form).
 */
export function mapServerPath(path: string, key: SuppliedKey, d: SuppliedDraft): string {
  let p = path.replace(/^supplied\.?/, "").replace(/\[(\d+)\]/g, ".$1");
  if (key !== "authors") { if (p === key) return ""; if (p.startsWith(`${key}.`)) p = p.slice(key.length + 1); }
  const rowsOf: Record<string, object[]> = { authors: d.authors, categories: d.categories, places: d.places, listings: d.listings, references: d.references, areas: d.areas, projects:d.projects };
  const m = /^([a-zA-Z]+)\.(\d+)(.*)$/.exec(p);
  if (m && rowsOf[m[1]!]) {
    const sent = used(rowsOf[m[1]!]!);
    const hit = sent[Number(m[2])];
    return hit ? `${m[1]}.${hit.index}${m[3]}` : m[1]!;
  }
  const keyed = (section: string, as: string, rows: { contentId: string }[]): string | null => {
    const k = new RegExp(`^${section}\\.([^.]+)(.*)$`).exec(p);
    if (!k) return null;
    const idx = rows.findIndex((r) => r.contentId === k[1]);
    return idx === -1 ? as : `${as}.${idx}${k[2]}`;
  };
  const found = (key === "newsroom" && keyed("releases", "releases", d.releases)) || (key === "wiki" && keyed("entries", "wikiEntries", d.wikiEntries)) || (key === "guide" && keyed("resources", "guideLinks", d.guideLinks)) || (key === "magazine" && keyed("items", "items", d.items));
  if (found) return found;
  if (/^topicDescriptions/.test(p)) return "topics";
  return p;
}

/** Import: a JSON object in the same shape the request takes, read into the draft. Returns what is wrong in plain words when it cannot be used. */
export function fromSupplied(key: SuppliedKey, value: unknown): { ok: true; draft: SuppliedDraft } | { ok: false; message: string } {
  if(key === "ppf") return {ok:false,message:"Use the PPF fields to edit this template. MCP can supply the documented PPF content contract directly."};
  const isO = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
  const s = (v: unknown): string => (typeof v === "string" ? v : "");
  const sl = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
  const rows = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? v.filter(isO) : []);
  const paras = (v: unknown): string => (Array.isArray(v) ? sl(v).join("\n\n") : s(v));
  if (!isO(value)) return { ok: false, message: "The JSON must be an object, for example { \"directory\": { ... } }." };
  const root = key === "authors" ? value : value[key];
  const allowed: Record<SuppliedKey, string[]> = { ppf:["ppf","profile"], authors: ["authors", "topicDescriptions"], directory: ["directory"], wiki: ["wiki"], guide: ["guide"], magazine: ["magazine"], cases:["cases"], newsroom:["newsroom"] };
  const extra = Object.keys(value).filter((k) => !allowed[key].includes(k));
  if (extra.length) return { ok: false, message: `This template accepts ${allowed[key].map((k) => `"${k}"`).join(" and ")}. These are not used by it: ${extra.slice(0, 5).join(", ")}.` };
  if (key !== "authors" && !isO(root)) return { ok: false, message: `Expected an object under "${key}".` };
  const r = root as Record<string, unknown>;
  const d = emptySupplied();
  const topics = (v: unknown) => (isO(v) ? Object.entries(v).map(([topic, description]): TopicNote => ({ topic, description: s(description) })) : []);
  const authors = (v: unknown) => rows(v).map((a): Author => ({ name: s(a["name"]), bio: s(a["bio"]), contentIds: sl(a["masterIds"]) }));
  if (key === "authors") { d.authors = authors(r["authors"]); d.topics = topics(r["topicDescriptions"]); }
  if (key === "directory") {
    d.categories = rows(r["categories"]).map((c): NameDesc => ({ name: s(c["name"]), description: s(c["description"]) }));
    d.places = rows(r["places"]).map((c): NameDesc => ({ name: s(c["name"]), description: s(c["description"]) }));
    d.listings = rows(r["listings"]).map((l): Listing => ({ name: s(l["name"]), category: s(l["category"]), place: s(l["place"]), summary: s(l["summary"]), description: paras(l["description"]), offerings: sl(l["offerings"]).join("\n"), phone: s(l["phone"]), email: s(l["email"]), addressLine: s(l["addressLine"]), websiteUrl: s(l["websiteUrl"]), featured: l["featured"] === true, confirmedOn: s(l["confirmedOn"]) }));
  }
  if (key === "wiki") {
    d.references = rows(r["references"]).map((x): Reference => ({ label: s(x["label"]), detail: s(x["detail"]), url: s(x["url"]) }));
    d.wikiEntries = isO(r["entries"]) ? Object.entries(r["entries"]).filter(([, m]) => isO(m)).map(([contentId, m]): WikiMeta => { const o = m as Record<string, unknown>; return { contentId, references: sl(o["references"]), related: sl(o["related"]), updatedOn: s(o["updatedOn"]) }; }) : [];
    d.topics = topics(r["topicDescriptions"]);
  }
  if (key === "guide") {
    d.areas = rows(r["areas"]).map((a): Area => ({ name: s(a["name"]), summary: s(a["summary"]), body: paras(a["body"]), services: sl(a["services"]), nearby: sl(a["nearby"]).join("\n") }));
    d.guideLinks = isO(r["resources"]) ? Object.entries(r["resources"]).filter(([, m]) => isO(m)).map(([contentId, m]): GuideLink => { const o = m as Record<string, unknown>; return { contentId, services: sl(o["services"]), areas: sl(o["areas"]) }; }) : [];
  }
  if (key === "magazine") {
    d.items = isO(r["items"]) ? Object.entries(r["items"]).filter(([, m]) => isO(m)).map(([contentId, m]): ItemMeta => { const o = m as Record<string, unknown>; const f = s(o["format"]); return { contentId, format: f === "article" || f === "guide" || f === "resource" ? f : "", featured: o["featured"] === true }; }) : [];
    d.authors = authors(r["authors"]); d.topics = topics(r["topicDescriptions"]);
  }
  if(key==='cases') {
    if(!Array.isArray(r.projects)||r.projects.some(p=>!isO(p)||Object.keys(p).some(k=>!['title','summary','challenge','approach','outcome','proofUrl','completedOn'].includes(k))))return {ok:false,message:'Cases require a projects list with only the supported project fields.'};
    d.projects=rows(r.projects).map(p=>({title:s(p.title),summary:s(p.summary),challenge:paras(p.challenge),approach:paras(p.approach),outcome:paras(p.outcome),proofUrl:s(p.proofUrl),completedOn:s(p.completedOn)}));
  }
  if(key==='newsroom') {
    if(!isO(r.releases)||Object.values(r.releases).some(v=>!isO(v)||Object.keys(v).some(k=>k!=='issuedOn')))return {ok:false,message:'Newsroom requires releases keyed by approved content ID, each with its issuedOn date.'};
    d.releases=Object.entries(r.releases).map(([contentId,v])=>({contentId,issuedOn:s((v as Record<string,unknown>).issuedOn)}));
  }
  return { ok: true, draft: d };
}

export const hasEntries = (d: SuppliedDraft): boolean => (!!ppfOutput(d.ppf) || Object.values(d).some((rows) => Array.isArray(rows) && used(rows as object[]).length > 0));
